import React, { useEffect, useState } from 'react';
import { App, Button, Input, Popover, Tooltip } from 'antd';
import { CloseOutlined, ShareAltOutlined } from '@ant-design/icons';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  LinkedinIcon,
} from 'react-share';

import {
  IconCopy,
  IconShareEmail,
  IconShareFacebook,
  IconShareInstagram,
  IconShareMessenger,
  IconShareReddit,
  IconShareWhatsapp,
  IconShareX,
} from '../Icon';

import styles from './index.module.scss';

export const openInNewTab = (url: string): void => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

  if (newWindow) newWindow.opener = null;
};

export const openInNewWindow = (url: string): void => {
  const width = 600;
  const height = 600;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  const newWindow = window.open(
    url,
    '_blank',
    // eslint-disable-next-line max-len
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=no,noopener,noreferrer`
  );

  if (newWindow) newWindow.opener = null;
};

type ShareWithComponent = {
  name: string;
  icon: React.ReactElement;
  Component: React.ComponentType<any>;
  props: (url: string, text: string) => Record<string, any>;
};

type ShareWithCustomLink = {
  name: string;
  icon: React.ReactElement;
  getBaseLink: (url: string, text: string) => string;
  manual?: boolean;
};

type SocialLink = ShareWithComponent | ShareWithCustomLink;

interface ShareButtonProps {
  label?: string;
}

export default function ShareButton({
  label,
}: ShareButtonProps): React.ReactElement {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      const cleanUrl = `${url.origin}${url.pathname}`;

      setShareUrl(
        cleanUrl.replace(
          'http://localhost:3000/',
          'https://dev.tippingguide.com/'
        )
      );
    }
  }, []);

  const shareText = `Take a look what I found here:\n${shareUrl}`;

  const handleCopy = async (mssg = 'Link copied to clipboard!') => {
    await navigator.clipboard
      .writeText(shareUrl)
      .then(async () => await message.success(mssg));
  };

  /* eslint-disable max-len */
  const socialLinks: SocialLink[] = [
    {
      name: 'Facebook',
      icon: <IconShareFacebook />,
      Component: FacebookShareButton,
      props: (url, text) => ({ url, quote: text }),
    },
    {
      name: 'Messenger',
      icon: <IconShareMessenger />,
      getBaseLink: (url) =>
        `https://www.messenger.com/t/?link=${encodeURIComponent(url)}`,
      manual: true,
    },
    {
      name: 'Whatsapp',
      icon: <IconShareWhatsapp />,
      // getBaseLink: (url, text) => `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`,
      Component: WhatsappShareButton,
      props: (url, text) => ({ url, title: text }),
    },
    {
      name: 'Email',
      icon: <IconShareEmail />,
      getBaseLink: (url, text) =>
        `mailto:?subject=${encodeURIComponent(
          'Take a look what I found here'
        )}&body=${encodeURIComponent(text + '\n\n' + url)}`,
    },
    {
      name: 'X (Twitter)',
      icon: <IconShareX />,
      // getBaseLink: (url, text) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text.replace(url, '').trim())}`, // Twitter usually appends the URL itself.
      Component: TwitterShareButton,
      props: (url, text) => ({
        url,
        title: text.replace(url, '').trim(),
      }),
    },
    {
      name: 'Reddit',
      icon: <IconShareReddit />,
      getBaseLink: (url, text) =>
        `https://www.reddit.com/submit?url=${encodeURIComponent(
          url
        )}&title=${encodeURIComponent(text.split('\n')[0])}`,
    },
    {
      name: 'Instagram',
      icon: <IconShareInstagram />,
      getBaseLink: () => 'https://www.instagram.com/',
      manual: true,
    },
    {
      name: 'LinkedIn',
      icon: (
        <LinkedinIcon
          style={{ borderRadius: '50%', height: '54px', width: '54px' }}
          bgStyle={{ fill: 'white' }}
          iconFillColor="rgb(40, 44, 53)"
        />
      ),
      Component: LinkedinShareButton,
      props: (url, text) => ({
        url,
        title: text.replace(url, '').trim(),
      }),
    },
  ];

  const content = (
    <div className={styles.popup}>
      <div className={styles.header}>
        <span>Share</span>
        <CloseOutlined
          className={styles.closeIcon}
          onClick={() => setOpen(false)}
        />
      </div>
      <div className={styles.socialIcons}>
        {socialLinks.map((item) => (
          <Tooltip title={item.name} key={item.name}>
            {'Component' in item ? (
              <item.Component
                {...item.props(shareUrl, shareText)}
                onClick={() => setOpen(false)}
              >
                <span className={styles.socialLinks}>
                  {item.icon}
                  <span>{item.name}</span>
                </span>
              </item.Component>
            ) : (
              <a
                href="#"
                className={styles.socialLinks}
                onClick={async (e) => {
                  e.preventDefault();
                  if (item.manual) {
                    handleCopy(
                      'Link copied to clipboard! App will open in a few seconds!'
                    ).then(() => {
                      openInNewWindow(item.getBaseLink(shareUrl, shareText));
                    });
                  } else {
                    handleCopy();
                    openInNewWindow(item.getBaseLink(shareUrl, shareText));
                  }
                  setOpen(false);
                }}
              >
                {item.icon}
                <span>{item.name}</span>
              </a>
            )}
          </Tooltip>
        ))}
      </div>
      <div className={styles.linkBox}>
        <Input
          id="share-input"
          value={shareUrl}
          readOnly
          className={styles.input}
        />
        <Button type="text" icon={<IconCopy />} onClick={() => handleCopy()} />
      </div>
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <Popover
        content={content}
        trigger="click"
        open={open}
        onOpenChange={setOpen}
      >
        <Button
          variant="outlined"
          icon={<ShareAltOutlined />}
          className={styles.btn}
        >
          {label || 'Share'}
        </Button>
      </Popover>
    </div>
  );
}
